import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import findColumn from 'data-frames/util/findAndAssertColumn';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {ConfigValidationFunction, AdditionalConfigValidationFunctions} from '../StepConfigValidationTypes';
import * as Helpers from './StepConfigValidationHelpers';

function getMaybeColumnDisplayName(dataFrameSpec: DataFrameSpec, systemName: string): string {
  const column = findColumn(dataFrameSpec, systemName);
  return column == null ? systemName : column.displayName;
}

const validateConfig: ConfigValidationFunction<PipelineStepType.PIVOT_DATASET, 0> = (
  config,
  {'Input dataset': [dataFrameSpec]}
) => {
  const duplicatePivotColumnsMsg = Helpers.getDuplicateColumnsMessage(
    dataFrameSpec,
    config.pivotColumns,
    'Cannot pivot by the same column more than once.'
  );
  const duplicateGroupColumnsMsg = Helpers.getDuplicateColumnsMessage(
    dataFrameSpec,
    config.groupColumns,
    'Cannot group by the same column more than once.'
  );

  // pivot and group by columns should not be used as value columns
  const valueColumnsUsedAsPivotColumns = config.valueColumns
    .map((valueColumn) => valueColumn.column)
    .filter((columnName) => config.pivotColumns.includes(columnName))
    .map((systemName) => getMaybeColumnDisplayName(dataFrameSpec, systemName));
  const valueColumnsUsedAsPivotColumnsMsg =
    valueColumnsUsedAsPivotColumns.length === 0
      ? null
      : `Cannot use a column both as a pivot column and a value column. Columns being used as both pivot and value: ${valueColumnsUsedAsPivotColumns}`;

  const valueColumnsUsedAsGroupByColumns = config.valueColumns
    .map((valueColumn) => valueColumn.column)
    .filter((columnName) => config.groupColumns.includes(columnName))
    .map((systemName) => getMaybeColumnDisplayName(dataFrameSpec, systemName));
  const valueColumnsUsedAsGroupByColumnsMsg =
    valueColumnsUsedAsGroupByColumns.length === 0
      ? null
      : `Cannot use a column both as a group by column and a value column. Columns being used as both group by and value: ${valueColumnsUsedAsGroupByColumns}`;

  return Helpers.coalesceMessages(
    duplicatePivotColumnsMsg,
    duplicateGroupColumnsMsg,
    valueColumnsUsedAsPivotColumnsMsg,
    valueColumnsUsedAsGroupByColumnsMsg
  );
};

export default {
  stepType: PipelineStepType.PIVOT_DATASET,
  stepVersion: 0,
  validateConfig,
} satisfies AdditionalConfigValidationFunctions<PipelineStepType.PIVOT_DATASET, 0>;
